import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { LegalContentRenderer } from "../components/LegalContentRenderer";
import { useAuth } from "../hooks/useAuth";
import {
  fetchAdminLegalDocument,
  fetchAdminLegalDefault,
  updateAdminLegalDocument,
} from "../api/legal";
import { C, font } from "../styles/tokens";

const TABS = [
  { slug: "terms", label: "Terms & Conditions", route: "/terms" },
  { slug: "privacy", label: "Privacy Policy", route: "/privacy" },
];

export function AdminLegalPage() {
  const { localUser } = useAuth();
  const navigate = useNavigate();
  const isAdmin = localUser?.type === "PLATFORM_ADMIN";

  const [activeTab, setActiveTab] = useState("terms");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Form state
  const [title, setTitle] = useState("");
  const [version, setVersion] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [content, setContent] = useState("");
  const [dbRecord, setDbRecord] = useState(null);
  const [defaultTemplate, setDefaultTemplate] = useState(null);

  // Preview options: 'split' | 'edit' | 'preview'
  const [viewMode, setViewMode] = useState("split");
  // Preview target: 'web' | 'mobile'
  const [previewDevice, setPreviewDevice] = useState("web");

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/admin");
      return;
    }
    loadDocument(activeTab);
  }, [isAdmin, activeTab]);

  async function loadDocument(slug) {
    setLoading(true);
    setStatusMsg(null);
    setErrorMsg(null);
    try {
      const data = await fetchAdminLegalDocument(slug);
      const doc = data.document;
      const tpl = data.defaultTemplate;
      setDbRecord(doc);
      setDefaultTemplate(tpl);

      if (doc) {
        setTitle(doc.title || tpl?.title || "");
        setVersion(doc.version || tpl?.version || "1.0");
        setEffectiveDate(doc.effectiveDate || tpl?.effectiveDate || "");
        setLastUpdated(doc.lastUpdated || tpl?.lastUpdated || "");
        setContent(doc.content || "");
      } else if (tpl) {
        setTitle(tpl.title || "");
        setVersion(tpl.version || "1.0");
        setEffectiveDate(tpl.effectiveDate || "");
        setLastUpdated(tpl.lastUpdated || "");
        setContent(tpl.content || "");
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.error?.message || "Failed to load document.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setStatusMsg(null);
    setErrorMsg(null);
    try {
      const updated = await updateAdminLegalDocument(activeTab, {
        title,
        version,
        effectiveDate,
        lastUpdated,
        content,
      });
      setDbRecord(updated);
      setStatusMsg("Document saved and published live! Website and mobile app views are updated immediately.");
    } catch (err) {
      setErrorMsg(err?.response?.data?.error?.message || "Failed to save document.");
    } finally {
      setSaving(false);
    }
  }

  async function handleResetToDefault() {
    if (
      !window.confirm(
        "Are you sure you want to load the default system template? Unsaved changes will be overwritten in the editor.",
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      const tpl = await fetchAdminLegalDefault(activeTab);
      if (tpl) {
        setTitle(tpl.title || "");
        setVersion(tpl.version || "1.0");
        setEffectiveDate(tpl.effectiveDate || "");
        setLastUpdated(tpl.lastUpdated || "");
        setContent(tpl.content || "");
        setStatusMsg("Default template loaded into editor. Click 'Publish Changes' when ready to go live.");
      }
    } catch (err) {
      setErrorMsg("Failed to fetch default template.");
    } finally {
      setLoading(false);
    }
  }

  function handleFileUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text === "string") {
        setContent(text);
        setStatusMsg(`File "${file.name}" uploaded into editor. Review the preview and click 'Publish Changes'.`);
      }
    };
    reader.onerror = () => {
      setErrorMsg("Failed to read file.");
    };
    reader.readAsText(file);
    // Reset file input
    e.target.value = "";
  }

  const currentTabObj = TABS.find((t) => t.slug === activeTab) || TABS[0];

  return (
    <AppShell>
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px 60px", fontFamily: font }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
              <Link to="/admin" style={{ color: C.textLight, textDecoration: "none", fontSize: 13, fontWeight: 500 }}>
                &larr; Admin Dashboard
              </Link>
              <span style={{ color: C.divider }}>/</span>
              <span style={{ color: C.textLight, fontSize: 13 }}>Legal &amp; Policy Management</span>
            </div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: "-0.5px" }}>
              Legal Documents &amp; Terms
            </h1>
            <p style={{ margin: "6px 0 0", color: C.textLight, fontSize: 14 }}>
              Update Terms &amp; Conditions and Privacy Policy live without code changes or website redeployment.
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              type="button"
              onClick={handleResetToDefault}
              disabled={loading || saving}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                border: `1px solid ${C.divider}`,
                background: "#fff",
                color: C.textDark,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reset to Default
            </button>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || saving}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                border: `1px solid ${C.divider}`,
                background: "#fff",
                color: C.textDark,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span>Upload Document</span>
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".txt,.md,.markdown,.html"
              style={{ display: "none" }}
            />

            <button
              type="button"
              onClick={handleSave}
              disabled={loading || saving}
              style={{
                padding: "9px 20px",
                borderRadius: 6,
                border: "none",
                background: saving ? C.textLight : C.primary,
                color: "#fff",
                fontSize: 14,
                fontWeight: 700,
                cursor: saving ? "not-allowed" : "pointer",
                boxShadow: "0 2px 6px rgba(0, 102, 204, 0.25)",
              }}
            >
              {saving ? "Publishing..." : "Publish Changes"}
            </button>
          </div>
        </div>

        {/* Tab switcher & Live Links */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `2px solid ${C.divider}`, marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", gap: 8 }}>
            {TABS.map((tab) => {
              const isActive = activeTab === tab.slug;
              return (
                <button
                  key={tab.slug}
                  onClick={() => setActiveTab(tab.slug)}
                  style={{
                    padding: "10px 18px",
                    border: "none",
                    borderBottom: isActive ? `3px solid ${C.primary}` : "3px solid transparent",
                    background: "none",
                    color: isActive ? C.primary : C.textLight,
                    fontWeight: isActive ? 700 : 500,
                    fontSize: 15,
                    cursor: "pointer",
                    marginBottom: -2,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", paddingBottom: 6 }}>
            <ActionLinkButton
              href={currentTabObj.route}
              label="Open Website View"
              title="Open full website view in a new tab"
              icon={
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              }
            />

            <ActionLinkButton
              href={`${currentTabObj.route}?view=app`}
              label="Open App WebView"
              title="Open in mobile App WebView mode (?view=app)"
              icon={
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                  <line x1="12" y1="18" x2="12.01" y2="18" />
                </svg>
              }
            />
          </div>
        </div>

        {/* Status Banners */}
        {statusMsg && (
          <div
            style={{
              padding: "12px 16px",
              borderRadius: 6,
              background: "#ecfdf5",
              border: "1px solid #10b981",
              color: "#065f46",
              fontSize: 14,
              marginBottom: 18,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{statusMsg}</span>
            <button
              onClick={() => setStatusMsg(null)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#065f46", fontWeight: 700 }}
            >
              &times;
            </button>
          </div>
        )}

        {errorMsg && (
          <div
            style={{
              padding: "12px 16px",
              borderRadius: 6,
              background: "#fef2f2",
              border: "1px solid #ef4444",
              color: "#991b1b",
              fontSize: 14,
              marginBottom: 18,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{errorMsg}</span>
            <button
              onClick={() => setErrorMsg(null)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#991b1b", fontWeight: 700 }}
            >
              &times;
            </button>
          </div>
        )}

        {/* Metadata Controls */}
        <div
          style={{
            background: "#f8fafc",
            border: `1px solid ${C.divider}`,
            borderRadius: 8,
            padding: 16,
            marginBottom: 20,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 14,
          }}
        >
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.textLight, marginBottom: 4 }}>
              Document Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. TickAndPass - Terms & Conditions"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.textLight, marginBottom: 4 }}>
              Version
            </label>
            <input
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g. 1.1"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.textLight, marginBottom: 4 }}>
              Effective Date
            </label>
            <input
              type="text"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              placeholder="e.g. August 1, 2026"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: C.textLight, marginBottom: 4 }}>
              Last Updated
            </label>
            <input
              type="text"
              value={lastUpdated}
              onChange={(e) => setLastUpdated(e.target.value)}
              placeholder="e.g. October 22, 2026"
              style={inputStyle}
            />
          </div>
        </div>

        {/* View Mode Toolbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 6, background: "#f1f5f9", padding: 3, borderRadius: 6 }}>
            <button
              type="button"
              onClick={() => setViewMode("edit")}
              style={viewModeBtn(viewMode === "edit")}
            >
              Editor Only
            </button>
            <button
              type="button"
              onClick={() => setViewMode("split")}
              style={viewModeBtn(viewMode === "split")}
            >
              Split View
            </button>
            <button
              type="button"
              onClick={() => setViewMode("preview")}
              style={viewModeBtn(viewMode === "preview")}
            >
              Preview Only
            </button>
          </div>

          {(viewMode === "split" || viewMode === "preview") && (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 12, color: C.textLight, fontWeight: 600 }}>Device Preview:</span>
              <button
                type="button"
                onClick={() => setPreviewDevice("web")}
                style={previewDeviceBtn(previewDevice === "web")}
              >
                Website View
              </button>
              <button
                type="button"
                onClick={() => setPreviewDevice("mobile")}
                style={previewDeviceBtn(previewDevice === "mobile")}
              >
                App WebView (Pixel 7a)
              </button>
            </div>
          )}
        </div>

        {/* Editor and Preview Area */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              viewMode === "split" ? "1fr 1fr" : "1fr",
            gap: 20,
            alignItems: "start",
          }}
        >
          {/* Editor Column */}
          {(viewMode === "split" || viewMode === "edit") && (
            <div
              style={{
                border: `1px solid ${C.divider}`,
                borderRadius: 8,
                background: "#fff",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <div
                style={{
                  background: "#f8fafc",
                  padding: "10px 16px",
                  borderBottom: `1px solid ${C.divider}`,
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                  color: C.textLight,
                  fontWeight: 600,
                }}
              >
                <span>MARKDOWN CONTENT EDITOR</span>
                <span>
                  {content.length.toLocaleString()} characters | {content.split(/\s+/).filter(Boolean).length} words
                </span>
              </div>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter markdown or text content here (supports # headings, - lists, **bold**, [links](url)...)"
                style={{
                  width: "100%",
                  height: 650,
                  boxSizing: "border-box",
                  border: "none",
                  padding: 16,
                  fontFamily: "Consolas, Menlo, Monaco, monospace",
                  fontSize: 13.5,
                  lineHeight: 1.6,
                  color: "#1e293b",
                  outline: "none",
                  resize: "vertical",
                  background: "#ffffff",
                }}
              />
            </div>
          )}

          {/* Preview Column */}
          {(viewMode === "split" || viewMode === "preview") && (
            <div
              style={{
                border: `1px solid ${C.divider}`,
                borderRadius: 8,
                background: previewDevice === "mobile" ? "#f1f5f9" : "#fff",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <div
                style={{
                  background: "#f8fafc",
                  padding: "10px 16px",
                  borderBottom: `1px solid ${C.divider}`,
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                  color: C.textLight,
                  fontWeight: 600,
                }}
              >
                <span>
                  LIVE PREVIEW &mdash; {previewDevice === "mobile" ? "App WebView (Google Pixel 7a)" : "Website View"}
                </span>
                <span>Real-Time Renderer</span>
              </div>

              {previewDevice === "mobile" ? (
                <div
                  style={{
                    background: "#f1f5f9",
                    padding: "20px 12px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    height: 650,
                    boxSizing: "border-box",
                  }}
                >
                  {/* Google Pixel 7a Smartphone Chassis */}
                  <div
                    style={{
                      position: "relative",
                      width: 360,
                      maxWidth: "100%",
                      height: "100%",
                    }}
                  >
                    {/* Pixel 7a Hardware Power Button */}
                    <div
                      style={{
                        position: "absolute",
                        right: -4,
                        top: 110,
                        width: 4,
                        height: 24,
                        background: "#334155",
                        borderRadius: "0 2px 2px 0",
                        zIndex: 0,
                      }}
                    />
                    {/* Pixel 7a Hardware Volume Rocker */}
                    <div
                      style={{
                        position: "absolute",
                        right: -4,
                        top: 155,
                        width: 4,
                        height: 52,
                        background: "#334155",
                        borderRadius: "0 2px 2px 0",
                        zIndex: 0,
                      }}
                    />

                    {/* Pixel 7a Phone Body */}
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        background: "#ffffff",
                        borderRadius: 28,
                        border: "9px solid #1a202c",
                        boxShadow:
                          "0 22px 45px -10px rgba(15, 23, 42, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.12), inset 0 0 0 1.5px #475569",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        position: "relative",
                        boxSizing: "border-box",
                      }}
                    >
                      {/* Top Ear Speaker Slit */}
                      <div
                        style={{
                          position: "absolute",
                          top: 2,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: 44,
                          height: 2,
                          background: "#4b5563",
                          borderRadius: 1,
                          zIndex: 30,
                        }}
                      />

                      {/* Android / Pixel 7a Status Bar */}
                      <div
                        style={{
                          height: 34,
                          background: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "0 16px",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#1f2937",
                          userSelect: "none",
                          flexShrink: 0,
                          position: "relative",
                        }}
                      >
                        {/* Time on left (Android style) */}
                        <span style={{ letterSpacing: "-0.2px", fontWeight: 700 }}>10:00</span>

                        {/* Centered Hole-Punch Camera (Pixel 7a Signature) */}
                        <div
                          style={{
                            position: "absolute",
                            left: "50%",
                            top: 8,
                            transform: "translateX(-50%)",
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            background: "#09090b",
                            border: "1px solid #27272a",
                            boxShadow: "inset 0 0 2px rgba(255,255,255,0.3)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div
                            style={{
                              width: 3.5,
                              height: 3.5,
                              borderRadius: "50%",
                              background: "#1e3a8a",
                            }}
                          />
                        </div>

                        {/* Right Status Icons: 5G, Wi-Fi, Cellular, Battery */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 800, color: "#1f2937", letterSpacing: "-0.3px" }}>5G</span>
                          {/* Android WiFi */}
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="#1f2937">
                            <path d="M12 4C7.31 4 3.07 5.9 0 8.98L12 21 24 8.98C20.93 5.9 16.69 4 12 4zm0 3.25c3.78 0 7.22 1.48 9.77 3.91L12 19.01 2.23 11.16C4.78 8.73 8.22 7.25 12 7.25z" />
                          </svg>
                          {/* Android Cellular Triangle */}
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="#1f2937">
                            <path d="M2 22h20V2z" />
                          </svg>
                          {/* Android Battery with % */}
                          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: "#1f2937" }}>98%</span>
                            <svg width="10" height="13" viewBox="0 0 24 24" fill="#1f2937">
                              <path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4z" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Android Material Top App Bar */}
                      <div
                        style={{
                          height: 48,
                          background: "#ffffff",
                          borderBottom: `1px solid ${C.divider}`,
                          display: "flex",
                          alignItems: "center",
                          padding: "0 14px",
                          gap: 12,
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            cursor: "pointer",
                            color: "#1f2937",
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="19" y1="12" x2="5" y2="12" />
                            <polyline points="12 19 5 12 12 5" />
                          </svg>
                        </div>
                        <span
                          style={{
                            flex: 1,
                            fontWeight: 700,
                            fontSize: 15,
                            color: C.navy,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {activeTab === "terms" ? "Terms & Conditions" : "Privacy Policy"}
                        </span>
                        {/* Android 3-dot overflow menu */}
                        <div style={{ display: "flex", flexDirection: "column", gap: 3, padding: "4px 6px", cursor: "pointer", opacity: 0.6 }}>
                          <div style={{ width: 3.5, height: 3.5, borderRadius: "50%", background: "#1f2937" }} />
                          <div style={{ width: 3.5, height: 3.5, borderRadius: "50%", background: "#1f2937" }} />
                          <div style={{ width: 3.5, height: 3.5, borderRadius: "50%", background: "#1f2937" }} />
                        </div>
                      </div>

                      {/* Scrollable WebView content */}
                      <div
                        style={{
                          flex: 1,
                          overflowY: "auto",
                          padding: "20px 16px 30px",
                          background: "#ffffff",
                        }}
                      >
                        <header
                          style={{
                            marginBottom: 20,
                            borderBottom: `1px solid ${C.divider}`,
                            paddingBottom: 14,
                          }}
                        >
                          <h1
                            style={{
                              fontSize: 22,
                              fontWeight: 800,
                              color: C.navy,
                              marginBottom: 8,
                              letterSpacing: "-0.4px",
                              lineHeight: 1.25,
                            }}
                          >
                            {title || "Untitled Document"}
                          </h1>
                          <div
                            style={{
                              fontSize: 11.5,
                              color: C.textLight,
                              display: "flex",
                              gap: 10,
                              flexWrap: "wrap",
                            }}
                          >
                            <span><strong>Effective:</strong> {effectiveDate || "Pending"}</span>
                            <span>|</span>
                            <span><strong>Updated:</strong> {lastUpdated || "Pending"}</span>
                            <span>|</span>
                            <span><strong>Version:</strong> {version || "1.0"}</span>
                          </div>
                        </header>

                        <LegalContentRenderer content={content} appView={true} />
                      </div>

                      {/* Android Gesture Navigation Pill */}
                      <div
                        style={{
                          height: 18,
                          background: "#ffffff",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            width: 76,
                            height: 3.5,
                            background: "#1f2937",
                            borderRadius: 2,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Desktop Webpage View in a Browser Window Frame */
                <div
                  style={{
                    height: 650,
                    display: "flex",
                    flexDirection: "column",
                    background: "#ffffff",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  {/* Browser Window Header Chrome */}
                  <div
                    style={{
                      background: "#f1f5f9",
                      borderBottom: `1px solid ${C.divider}`,
                      padding: "8px 14px",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      flexShrink: 0,
                      userSelect: "none",
                    }}
                  >
                    {/* Window Control Buttons */}
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <div style={{ width: 11, height: 11, borderRadius: "50%", background: "#ef4444" }} />
                      <div style={{ width: 11, height: 11, borderRadius: "50%", background: "#f59e0b" }} />
                      <div style={{ width: 11, height: 11, borderRadius: "50%", background: "#10b981" }} />
                    </div>

                    {/* Navigation Buttons (Back / Forward / Reload) */}
                    <div style={{ display: "flex", gap: 8, alignItems: "center", color: "#64748b" }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5, cursor: "not-allowed" }}>
                        <line x1="19" y1="12" x2="5" y2="12" />
                        <polyline points="12 19 5 12 12 5" />
                      </svg>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5, cursor: "not-allowed" }}>
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: "pointer" }}>
                        <path d="M23 4v6h-6" />
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                      </svg>
                    </div>

                    {/* Omnibox / URL Address Bar */}
                    <div
                      style={{
                        flex: 1,
                        background: "#ffffff",
                        borderRadius: 6,
                        border: "1px solid #cbd5e1",
                        padding: "5px 12px",
                        fontSize: 12,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                      <div style={{ flex: 1, fontFamily: "monospace", color: "#334155" }}>
                        <span style={{ color: "#94a3b8" }}>https://</span>
                        <span style={{ color: "#475569" }}>www.tickandpass.com</span>
                        <span style={{ color: C.navy, fontWeight: 700 }}>{activeTab === "terms" ? "/terms" : "/privacy"}</span>
                      </div>
                      <span
                        style={{
                          fontSize: 10,
                          background: "#ecfdf5",
                          color: "#065f46",
                          padding: "1px 6px",
                          borderRadius: 4,
                          fontWeight: 700,
                        }}
                      >
                        SSL
                      </span>
                    </div>
                  </div>

                  {/* Scrollable Public Website Page Viewport */}
                  <div
                    style={{
                      flex: 1,
                      overflowY: "auto",
                      background: "#ffffff",
                    }}
                  >
                    {/* Public Website Header (Header Chrome) */}
                    <div
                      style={{
                        borderBottom: `1px solid ${C.divider}`,
                        background: "#ffffff",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                      }}
                    >
                      {/* Top tabs strip */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "6px 28px",
                          borderBottom: `1px solid #f1f5f9`,
                          fontSize: 12,
                          color: C.textLight,
                        }}
                      >
                        <div style={{ display: "flex", gap: 16 }}>
                          <span style={{ color: C.primary, fontWeight: 700, cursor: "pointer" }}>Shop</span>
                          <span style={{ cursor: "pointer" }}>Gateway</span>
                          <span style={{ cursor: "pointer" }}>User Login</span>
                        </div>
                        <div style={{ display: "flex", gap: 16 }}>
                          <span style={{ cursor: "pointer" }}>For Businesses</span>
                          <span style={{ cursor: "pointer" }}>For Academic Institutions</span>
                        </div>
                      </div>

                      {/* Main Bar with Brand Logo */}
                      <div
                        style={{
                          padding: "12px 28px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <img
                            src="/brand-lockup.png"
                            alt="Tick & Pass"
                            style={{ height: 32, width: "auto", display: "block" }}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                          <span style={{ fontSize: 20, fontWeight: 800, color: C.navy, letterSpacing: "-0.5px" }}>
                            Tick<span style={{ color: C.primary }}>&amp;</span>Pass
                          </span>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <span
                            style={{
                              padding: "6px 14px",
                              borderRadius: 6,
                              border: `1px solid ${C.primary}`,
                              color: C.primary,
                              fontSize: 13,
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            Sign In
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Website Document Content */}
                    <div style={{ maxWidth: 860, margin: "0 auto", padding: "40px 24px 60px" }}>
                      <header
                        style={{
                          marginBottom: 32,
                          borderBottom: `1px solid ${C.divider}`,
                          paddingBottom: 20,
                        }}
                      >
                        <h1
                          style={{
                            fontSize: 34,
                            fontWeight: 800,
                            color: C.navy,
                            marginBottom: 12,
                            letterSpacing: "-0.6px",
                            lineHeight: 1.2,
                          }}
                        >
                          {title || "Untitled Document"}
                        </h1>
                        <div
                          style={{
                            fontSize: 13,
                            color: C.textLight,
                            display: "flex",
                            gap: 16,
                            flexWrap: "wrap",
                          }}
                        >
                          <span><strong>Effective Date:</strong> {effectiveDate || "Pending"}</span>
                          <span>|</span>
                          <span><strong>Last Updated:</strong> {lastUpdated || "Pending"}</span>
                          <span>|</span>
                          <span><strong>Version:</strong> {version || "1.0"}</span>
                        </div>
                      </header>

                      <LegalContentRenderer content={content} appView={false} />
                    </div>

                    {/* Public Website Footer */}
                    <footer
                      style={{
                        background: C.navyDark || "#0E1C36",
                        color: "#AAB8D0",
                        padding: "24px 28px",
                        marginTop: 40,
                        fontSize: 12,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 12,
                      }}
                    >
                      <span>&copy; {new Date().getFullYear()} TickAndPass Services Private Limited. All rights reserved.</span>
                      <div style={{ display: "flex", gap: 16, color: "#cbd5e1" }}>
                        <span>Terms &amp; Conditions</span>
                        <span>Privacy Policy</span>
                        <span>Contact Support</span>
                      </div>
                    </footer>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "8px 12px",
  borderRadius: 6,
  border: `1px solid ${C.divider}`,
  fontSize: 13,
  color: C.textDark,
  outline: "none",
  background: "#fff",
};

const viewModeBtn = (active) => ({
  border: "none",
  padding: "5px 12px",
  borderRadius: 4,
  fontSize: 12,
  fontWeight: active ? 700 : 500,
  background: active ? "#fff" : "transparent",
  color: active ? C.navy : C.textLight,
  cursor: "pointer",
  boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
});

const previewDeviceBtn = (active) => ({
  border: "none",
  padding: "4px 10px",
  borderRadius: 4,
  fontSize: 12,
  fontWeight: active ? 700 : 500,
  background: active ? C.navy : "#f1f5f9",
  color: active ? "#fff" : C.textDark,
  cursor: "pointer",
});

function ActionLinkButton({ href, label, icon, title }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 16px",
        borderRadius: 6,
        border: `1px solid ${hovered ? C.primary : C.dividerStrong || "#D3DAE6"}`,
        background: hovered ? "#f0fdf4" : "#ffffff",
        color: hovered ? C.primary : C.textDark,
        fontSize: 13,
        fontWeight: 600,
        textDecoration: "none",
        boxShadow: hovered
          ? "0 3px 8px rgba(8, 131, 62, 0.15)"
          : "0 1px 2px rgba(0, 0, 0, 0.05)",
        cursor: "pointer",
        transition: "all 0.15s ease",
        transform: hovered ? "translateY(-1px)" : "none",
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          color: hovered ? C.primary : "#64748b",
          transition: "color 0.15s ease",
        }}
      >
        {icon}
      </span>
      <span>{label}</span>
    </a>
  );
}
